import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../Redux/Actions/SnackbarActions/SnackbarActions";
import PharmacySnackbar from "../../../Utils/Snackbars/SnackbarUtil";
import { PLContainer } from "../../../Utils/styledComponents";
import { usePrint } from "../../../Utils/Templates/usePrint";
import { BackButton } from "../../../Utils/Buttons/DataExportationButton";
import { PrintPdfButtons } from "./PrintPdfButtons";
import companyAddress from "../../../Helpers/companyAddress.json";
import Card from "@material-ui/core/Card";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import ReusableDashboardHeader from "../../../Containers/Dasboard/ReusableDashboardHeader";
import { history } from "../../../Helpers/history";
import { useQuery } from "react-query";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { useCompany } from "../../../Context/company-context";
import { LoadingGif } from "../../../Utils/Loader";
import { NoTransaction } from "../COA/ReusableJournalTable";
import { formatAmount } from "../../../Utils/formatAmount";
import { reverseDate, convertDate } from "../../../Utils/ConvertDate";

const useStyles = makeStyles((theme) => ({
  headerTitle: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    textAlign: "center",
    margin: "20px 0 10px",
  },
  periodText: {
    textAlign: "center",
    fontSize: "1.1rem",
    marginBottom: "20px",
  },
  sectionHeader: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    fontSize: "1.1rem",
    padding: "12px 16px",
  },
  itemRow: {
    "& td": {
      padding: "8px 40px 8px 60px", // Indent line items
    },
  },
  adjustmentHeader: {
    "& td": {
      padding: "10px 40px 6px 60px",
      fontWeight: "bold",
      fontStyle: "italic",
    },
  },
  amountCell: {
    textAlign: "right",
    width: "200px",
    paddingRight: "40px",
    fontWeight: "500",
  },
  totalRow: {
    borderTop: "3px double #000",
    fontWeight: "bold",
    backgroundColor: "#f9f9f9",
  },
  finalTotalRow: {
    borderTop: "4px double #000",
    fontWeight: "bold",
    fontSize: "1.2rem",
    backgroundColor: "#e6f3ff",
  },
  notesSection: {
    marginTop: "30px",
    padding: "16px",
    fontSize: "0.9rem",
    lineHeight: "1.6",
    borderTop: "1px solid #ddd",
  },
}));

const CashFlowStatementDetailed = ({ actions, snackbars }) => {
  const classes = useStyles();
  const { state } = useCompany();
  const { logo } = state;
  const { img_url: defaultImage } = companyAddress;

  const formatAmountWithParentheses = (amount) => {
    if (amount === undefined || amount === null) return formatAmount(0);
    const formatted = formatAmount(Math.abs(amount));
    return amount < 0 ? `(${formatted})` : formatted;
  };

  const today = convertDate();
  const [dates, setDates] = useState({
    startDate: reverseDate(today),
    endDate: reverseDate(today),
  });

  const { isLoading, isError, isSuccess, data } = useQuery(
    ["detailedCashFlow", dates],
    async () => {
      const response = await axios.get(
        `${config.epharmUrl}/accounting/detailed_cash_flow_statement`,
        {
          params: {
            start_date: dates.startDate,
            end_date: dates.endDate,
          },
        }
      );
      return response.data?.data || {};
    },
    { retry: 1 }
  );

  const { open, variant, message } = snackbars;
  const { componentRef, handlePrint } = usePrint("CashFlowStatementDetailed");

  const cashFlow = data?.cash_flow_statement || {};
  const generatedOn = data?.generated_on || "";
  const period = data?.period || `${dates.startDate} to ${dates.endDate}`;

  const handleChangeDate = (e) => {
    setDates({ ...dates, [e.target.name]: e.target.value });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="journals">
      <ReusableDashboardHeader
        component="Cash Flow Statement"
        heading="Accounting"
        subHeading="Cash Flow Statement - Detailed"
        link={history.location.pathname}
      />

      <PharmacySnackbar message={message} open={open} variant={variant} />

      <div className="d-flex justify-content-start align-items-center ml-4 mt-3">
        <BackButton to="/allreports" text="Reports" />
      </div>

      <PLContainer>
        {isLoading && (
          <div className="text-center mt-5">
            <LoadingGif />
          </div>
        )}

        {isSuccess && cashFlow ? (
          <Card className="mx-2 my-3">
            <div
              style={{
                padding: "8px 16px",
                borderBottom: "1px solid rgba(224,224,224,1)",
              }}
              className="d-flex justify-content-between align-items-center"
            >
              <div className="d-flex">
                <div className="form-group d-flex align-items-center">
                  <label htmlFor="startDate">From&nbsp;</label>
                  <input type="date" name="startDate" value={dates.startDate} onChange={handleChangeDate} className="form-control" />
                </div>
                <div className="form-group d-flex align-items-center ml-4">
                  <label htmlFor="endDate">To&nbsp;</label>
                  <input type="date" name="endDate" value={dates.endDate} onChange={handleChangeDate} className="form-control" />
                </div>
              </div>
              <div>
                <PrintPdfButtons downloadPdf={() => {}} handlePrint={handlePrint} />
              </div>
            </div>

            <div className="p-4">
              <div ref={componentRef}>
                {/* Company Header */}
                <table className="table table-sm table-borderless w-100">
                  <tbody>
                    <tr>
                      <td align="center">
                        <img src={logo ?? defaultImage} alt="Company Logo" className="img-fluid" style={{ maxHeight: "80px" }} />
                      </td>
                    </tr>
                    <tr>
                      <td align="center" className={classes.headerTitle}>
                        CASH FLOW STATEMENT
                      </td>
                    </tr>
                    <tr>
                      <td align="center" className={classes.periodText}>
                        For the period {period}
                      </td>
                    </tr>
                    <tr>
                      <td align="center">
                        <small>Generated on: {formatDateTime(generatedOn)}</small>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <Table size="small">
                  <TableBody>
                    {/* Operating Activities */}
                    <TableRow>
                      <TableCell colSpan={2} className={classes.sectionHeader}>
                        Cash flows from operating activities
                      </TableCell>
                    </TableRow>

                    {/* Net Income */}
                    <TableRow className={classes.itemRow}>
                      <TableCell>Net Income</TableCell>
                      <TableCell className={classes.amountCell}>
                        {formatAmountWithParentheses(cashFlow.operating_activities?.items?.[0]?.amount)}
                      </TableCell>
                    </TableRow>

                    {/* Adjustments Header */}
                    <TableRow className={classes.adjustmentHeader}>
                      <TableCell>Adjustments for:</TableCell>
                      <TableCell></TableCell>
                    </TableRow>

                    {/* Working Capital Changes */}
                    {cashFlow.operating_activities?.items?.slice(1).map((item, idx) => (
                      <TableRow key={idx} className={classes.itemRow}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className={classes.amountCell}>
                          {formatAmountWithParentheses(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}

                    <TableRow className={classes.totalRow}>
                      <TableCell><strong>Net cash provided by (used in) operating activities</strong></TableCell>
                      <TableCell className={classes.amountCell}>
                        <strong>{formatAmountWithParentheses(cashFlow.operating_activities?.net_cash_from_operating)}</strong>
                      </TableCell>
                    </TableRow>

                    {/* Investing Activities */}
                    <TableRow>
                      <TableCell colSpan={2} className={classes.sectionHeader}>
                        Cash flows from investing activities
                      </TableCell>
                    </TableRow>
                    {cashFlow.investing_activities?.items?.map((item, idx) => (
                      <TableRow key={idx} className={classes.itemRow}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className={classes.amountCell}>
                          {formatAmountWithParentheses(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className={classes.totalRow}>
                      <TableCell><strong>Net cash provided by (used in) investing activities</strong></TableCell>
                      <TableCell className={classes.amountCell}>
                        <strong>{formatAmountWithParentheses(cashFlow.investing_activities?.net_cash_from_investing)}</strong>
                      </TableCell>
                    </TableRow>

                    {/* Financing Activities */}
                    <TableRow>
                      <TableCell colSpan={2} className={classes.sectionHeader}>
                        Cash flows from financing activities
                      </TableCell>
                    </TableRow>
                    {cashFlow.financing_activities?.items?.map((item, idx) => (
                      <TableRow key={idx} className={classes.itemRow}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className={classes.amountCell}>
                          {formatAmountWithParentheses(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className={classes.totalRow}>
                      <TableCell><strong>Net cash provided by (used in) financing activities</strong></TableCell>
                      <TableCell className={classes.amountCell}>
                        <strong>{formatAmountWithParentheses(cashFlow.financing_activities?.net_cash_from_financing)}</strong>
                      </TableCell>
                    </TableRow>

                    {/* Final Net Change */}
                    <TableRow className={classes.finalTotalRow}>
                      <TableCell><strong>Net increase (decrease) in cash and cash equivalents</strong></TableCell>
                      <TableCell className={classes.amountCell}>
                        <strong>{formatAmountWithParentheses(cashFlow.net_change_in_cash)}</strong>
                      </TableCell>
                    </TableRow>

                    {/* Optional: Add beginning/ending balance later */}
                    <TableRow>
                      <TableCell>Cash and cash equivalents at beginning of period</TableCell>
                      <TableCell className={classes.amountCell}>[To be calculated]</TableCell>
                    </TableRow>
                    <TableRow className={classes.finalTotalRow}>
                      <TableCell><strong>Cash and cash equivalents at end of period</strong></TableCell>
                      <TableCell className={classes.amountCell}>
                        <strong>[To be calculated]</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <div className={classes.notesSection}>
                  <h6><strong>Notes:</strong></h6>
                  <ul>
                    <li>Negative amounts are shown in parentheses, as is standard in financial statements.</li>
                    <li>This statement is prepared using the indirect method.</li>
                    <li>Cash and cash equivalents include petty cash, bank accounts, and mobile money balances.</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        ) : !isLoading ? (
          <NoTransaction text="cash flow data" />
        ) : null}

        {isError && <div className="text-center text-danger">Failed to load cash flow statement. Please try again.</div>}
      </PLContainer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  snackbars: state.snackbars,
});

const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(mapStateToProps, mapDispatchToProps)(CashFlowStatementDetailed);